import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, priceLabel } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const FILTERS = [
  { key: "orologi", label: "Orologi" },
  { key: "gioielli", label: "Gioielli" },
];

const JEWELRY_FILTERS = [
  { key: "tutti", label: "Tutti i Gioielli", terms: [] },
  { key: "anelli", label: "Anelli", terms: ["anelli"] },
  { key: "bracciali", label: "Bracciali", terms: ["bracciali"] },
  { key: "collane", label: "Collane", terms: ["collane"] },
  { key: "brillanti", label: "Brillanti", terms: ["brillanti"] },
  { key: "pietre", label: "Pietre Preziose", terms: ["pietre"] },
];

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawCategoria = searchParams.get("categoria");
  const categoria = rawCategoria === "gioielli" ? "gioielli" : "orologi";
  const tipoGioiello = categoria === "gioielli" ? searchParams.get("tipo") || "tutti" : "tutti";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category: categoria } })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [categoria]);

  const activeJewelFilter = JEWELRY_FILTERS.find((f) => f.key === tipoGioiello) || JEWELRY_FILTERS[0];
  const visibleProducts =
    categoria === "gioielli" && activeJewelFilter.terms.length > 0
      ? products.filter((p) => activeJewelFilter.terms.some((term) => p.name.toLowerCase().includes(term)))
      : products;

  return (
    <div className="px-5 pb-24 pt-28 sm:px-6 lg:px-12 lg:pb-32 lg:pt-36" data-testid="catalog-page">
      <Reveal y={24}>
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">La Collezione</p>
        <h1 className="mt-4 font-serif text-4xl font-light tracking-tight text-ivory lg:text-7xl">
          {categoria === "gioielli" ? "Gioielli" : "Orologi"}
        </h1>
      </Reveal>

      <div className="mt-10 flex flex-nowrap gap-3 overflow-x-auto pb-2 lg:mt-12 lg:flex-wrap lg:overflow-visible lg:pb-0">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setSearchParams({ categoria: f.key })}
            className={`min-h-11 shrink-0 rounded-full border px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-500 ${
              categoria === f.key
                ? "border-gold bg-gold text-void"
                : "border-line text-muted hover:border-gold hover:text-gold"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {categoria === "gioielli" && (
        <div className="mt-6 flex flex-nowrap gap-3 overflow-x-auto pb-2 lg:flex-wrap lg:overflow-visible lg:pb-0" data-testid="jewelry-subfilters">
          {JEWELRY_FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`jewel-filter-${f.key}`}
              onClick={() => setSearchParams(f.key === "tutti" ? { categoria: "gioielli" } : { categoria: "gioielli", tipo: f.key })}
              className={`min-h-11 shrink-0 rounded-full border px-5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-500 ${
                tipoGioiello === f.key
                  ? "border-gold bg-gold/15 text-gold"
                  : "border-line text-muted hover:border-gold hover:text-gold"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="mt-24 font-serif text-2xl italic text-muted" data-testid="catalog-loading">
          Caricamento della collezione…
        </p>
      ) : visibleProducts.length === 0 ? (
        <p className="mt-24 font-serif text-2xl italic text-muted" data-testid="catalog-empty">
          Nessun pezzo disponibile in questa categoria al momento.
        </p>
      ) : (
        <div className="mt-16 grid gap-x-8 gap-y-20 sm:grid-cols-2 lg:grid-cols-12" data-testid="catalog-grid">
          {visibleProducts.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.08} className={`sm:col-span-1 ${SPANS[i % 4]}`}>
              <Link to={`/prodotto/${p.id}`} data-testid={`catalog-product-${p.reference}`} className="group block">
                <div className="spotlight relative overflow-hidden bg-elevated">
                  <img
                    src={p.image}
                    alt={p.name}
                    className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                    p.category === "orologi" ? "aspect-square sm:aspect-[4/5]" : "aspect-[4/5]"
                  }`}
                  />
                </div>
                <div className="mt-5 flex items-baseline justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gold">{p.reference}</p>
                    <h3 className="mt-1 font-serif text-2xl text-ivory">{p.name}</h3>
                  </div>
                  <p className="shrink-0 font-mono text-sm text-gold">{priceLabel(p)}</p>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted">{p.condition}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
